// Client identity + API helpers
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const CLIENT_KEY = "bingo_client_id";

export function ensureClientId() {
  let id = localStorage.getItem(CLIENT_KEY);
  if (!id) {
    id = (crypto?.randomUUID?.() || `c_${Date.now()}_${Math.random().toString(36).slice(2)}`);
    localStorage.setItem(CLIENT_KEY, id);
  }
  return id;
}

export function getClientId() {
  return localStorage.getItem(CLIENT_KEY) || ensureClientId();
}

export const api = axios.create({ baseURL: API });

export async function fetchUsage() {
  const id = getClientId();
  const { data } = await api.get(`/usage/${id}`);
  return data;
}

export async function trackUsage(cardsCount = 1) {
  const id = getClientId();
  const { data } = await api.post("/usage/track", { client_id: id, cards_count: cardsCount });
  return data;
}

// Fire-and-forget log of a CREATED (generated) bingo so the owner can see
// every bingo made in the admin — even ones never saved/shared. Anonymous;
// errors are swallowed so it never blocks generation.
export async function logGeneration(meta = {}) {
  try {
    const id = getClientId();
    await api.post("/generations/log", { client_id: id, ...meta });
  } catch { /* non-blocking */ }
}

// Fire-and-forget log of an arrival from the Android app (CTA carries
// ?utm_source=android_app&ref=APP). Anonymous; errors swallowed.
export async function logAppVisit(meta = {}) {
  try {
    const id = getClientId();
    await api.post("/app-visits/log", { client_id: id, ...meta });
  } catch { /* non-blocking */ }
}
export async function adminAppVisits(token, limit = 50, skip = 0) {
  const { data } = await api.get(`/admin/app-visits?token=${encodeURIComponent(token)}&limit=${limit}&skip=${skip}`);
  return data;
}

// Fire-and-forget log of a Bingo Caller game start (offline or TV/online).
// Anonymous: kind/size/language/platform only. Errors swallowed.
export async function logCallerStart(meta = {}) {
  try {
    const id = getClientId();
    await api.post("/caller/log-start", { client_id: id, ...meta });
  } catch { /* non-blocking */ }
}
export async function adminCallerStarts(token, limit = 50, skip = 0) {
  const { data } = await api.get(`/admin/caller-starts?token=${encodeURIComponent(token)}&limit=${limit}&skip=${skip}`);
  return data;
}

// Two-screen caller session (TV display + phone/laptop controller).
export async function callerCreateSession(max, lang = "nl", skin = "classic") {
  const { data } = await api.post("/caller/session/create", { max, lang, skin });
  return data;
}
export async function callerGetSession(code) {
  const { data } = await api.get(`/caller/session/${encodeURIComponent(code)}`);
  return data;
}
export async function callerDraw(code) {
  const { data } = await api.post(`/caller/session/${encodeURIComponent(code)}/draw`);
  return data;
}
export async function callerResetSession(code) {
  const { data } = await api.post(`/caller/session/${encodeURIComponent(code)}/reset`);
  return data;
}
export async function callerConnect(code) {
  const { data } = await api.post(`/caller/session/${encodeURIComponent(code)}/connect`);
  return data;
}
export async function callerUpdateSettings(code, settings) {
  const { data } = await api.post(`/caller/session/${encodeURIComponent(code)}/settings`, settings);
  return data;
}

// Play Store tester sign-up (hidden /testers page).
export async function applyTester(email, name, lang) {
  const { data } = await api.post("/testers/apply", { email, name, lang });
  return data;
}

export async function getUnlockPackage() {
  const { data } = await api.get("/packages/unlock");
  return data;
}

// Fetch the 3-tier pricing staffel from the server. Used by the paywall
// modal (tier picker) and by the landing page pricing section so the
// numbers displayed to the user always match what's actually charged.
export async function getPricingTiers() {
  const { data } = await api.get("/pricing/tiers");
  return data;
}

export async function createCheckout(promoCode = null, tier = "small") {
  const id = getClientId();
  const origin = window.location.origin;
  const { data } = await api.post("/payments/checkout", {
    client_id: id,
    origin_url: origin,
    promo_code: promoCode || null,
    tier: tier || "small",
  });
  return data;
}

export async function validatePromo(code) {
  const id = getClientId();
  const { data } = await api.post("/promo/validate", { code, client_id: id });
  return data;
}

export async function applyPromo(code) {
  const id = getClientId();
  const { data } = await api.post("/promo/apply", { code, client_id: id });
  return data;
}

export async function createProPlusCheckout() {
  const id = getClientId();
  const origin = window.location.origin;
  const { data } = await api.post("/payments/checkout-pro-plus", { client_id: id, origin_url: origin });
  return data;
}

export async function devUnlockProPlus() {
  const id = getClientId();
  const { data } = await api.post("/dev/unlock-pro-plus", { client_id: id, origin_url: window.location.origin });
  return data;
}

// === Music (iTunes/Apple preview + artwork) ===
export async function musicResolve(songs, country) {
  const { data } = await api.post("/music/resolve", { songs, country });
  return data;
}
export async function musicSearch(q, country) {
  const { data } = await api.get("/music/search", { params: { q, country } });
  return data;
}
export async function setPresentState(code, state) {
  const { data } = await api.post(`/cards/${code}/present`, state);
  return data;
}
export async function getPresentState(code) {
  const { data } = await api.get(`/cards/${code}/present`);
  return data;
}
export async function touchPresence(code) {
  const { data } = await api.post(`/cards/${code}/touch`, { client_id: getClientId() });
  return data;
}
export async function getJoinedCount(code) {
  const { data } = await api.get(`/cards/${code}/joined-count`);
  return data;
}

// === Spotify ===
export async function spotifyLogin(redirectBack) {
  const id = getClientId();
  const { data } = await api.post("/spotify/login", { client_id: id, redirect_back: redirectBack });
  return data;
}

export async function spotifyDevLogin() {
  const id = getClientId();
  const { data } = await api.post("/spotify/dev-login", { client_id: id, redirect_back: "/" });
  return data;
}

export async function spotifySession() {
  const id = getClientId();
  const { data } = await api.get(`/spotify/session/${id}`);
  return data;
}

export async function spotifyDisconnect() {
  const id = getClientId();
  const { data } = await api.post(`/spotify/disconnect/${id}`);
  return data;
}

export async function spotifyToken() {
  const id = getClientId();
  const { data } = await api.get(`/spotify/token/${id}`);
  return data;
}

export async function spotifySearchAndPlay({ query, artist, title, deviceId = null } = {}) {
  const id = getClientId();
  const { data } = await api.post("/spotify/search-and-play", {
    client_id: id,
    query: query || null,
    artist: artist || null,
    title: title || null,
    device_id: deviceId,
  });
  return data;
}

export async function spotifyPause(deviceId = null) {
  const id = getClientId();
  const params = deviceId ? `?device_id=${encodeURIComponent(deviceId)}` : "";
  const { data } = await api.post(`/spotify/pause/${id}${params}`);
  return data;
}

export async function spotifyResume(deviceId = null) {
  const id = getClientId();
  const params = deviceId ? `?device_id=${encodeURIComponent(deviceId)}` : "";
  const { data } = await api.post(`/spotify/resume/${id}${params}`);
  return data;
}

export async function spotifySkip(deviceId = null) {
  const id = getClientId();
  const params = deviceId ? `?device_id=${encodeURIComponent(deviceId)}` : "";
  const { data } = await api.post(`/spotify/skip/${id}${params}`);
  return data;
}

export async function spotifyPrevious(deviceId = null) {
  const id = getClientId();
  const params = deviceId ? `?device_id=${encodeURIComponent(deviceId)}` : "";
  const { data } = await api.post(`/spotify/previous/${id}${params}`);
  return data;
}

export async function spotifyTransfer(deviceId) {
  const id = getClientId();
  const { data } = await api.put("/spotify/transfer", { client_id: id, device_id: deviceId });
  return data;
}

export async function spotifyDevices() {
  const id = getClientId();
  const { data } = await api.get(`/spotify/devices/${id}`);
  return data;
}

export async function spotifySetPreferredDevice(deviceId, deviceName) {
  const id = getClientId();
  const { data } = await api.post(`/spotify/preferred-device/${id}`, {
    client_id: id,
    device_id: deviceId,
    device_name: deviceName || null,
  });
  return data;
}

export async function spotifyImportPlaylist(playlistUrl) {
  const id = getClientId();
  const { data } = await api.post("/spotify/playlist-import", { client_id: id, playlist_url: playlistUrl });
  return data;
}

// Owner-only unlock via secret URL (?owner=<ADMIN_TOKEN>): grants unlimited
// PRO + PRO+ for this browser so the owner can host free on mobile.
export async function ownerUnlock(token) {
  const id = getClientId();
  const { data } = await api.post("/dev/owner-unlock", { client_id: id, token });
  return data;
}

// Affiliate/referral: record a click + remember the partner for this browser.
export async function affiliateTrack(code) {
  const id = getClientId();
  const { data } = await api.post("/affiliate/track", { code, client_id: id });
  return data;
}
export async function affiliateList(token) {
  const { data } = await api.get(`/affiliate/admin/list?token=${encodeURIComponent(token)}`);
  return data;
}
export async function affiliateCreate(token, payload) {
  const { data } = await api.post(`/affiliate/admin/create?token=${encodeURIComponent(token)}`, payload);
  return data;
}

export async function restorePurchase(email) {
  const id = getClientId();
  const { data } = await api.post("/payments/restore", { client_id: id, email });
  return data;
}

export async function getPaymentStatus(sessionId) {
  const { data } = await api.get(`/payments/status/${sessionId}`);
  return data;
}

export async function claimBingo(code, cardNumber, playerName) {
  const { data } = await api.post(`/cards/${code}/claim`, { card_number: cardNumber, player_name: playerName });
  return data;
}

export async function listClaims(code) {
  const { data } = await api.get(`/cards/${code}/claims`);
  return data;
}

export async function updateClaimStatus(code, claimId, status) {
  const { data } = await api.post(`/cards/${code}/claims/${claimId}/status`, { status });
  return data;
}

export async function devGrantPro() {
  const id = getClientId();
  const { data } = await api.post(`/dev/grant-pro`, { client_id: id });
  return data;
}
